import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePostHog } from '@posthog/react';
import { capture, FORGOT_PASSWORD_PAGE_EVENT } from '../analytics/posthog';
import styles from './ForgotPassword.module.css';
import BrandLogo from './BrandLogo';
import config, { logger } from '../config';
import { getApiHeaders } from '../utils/api';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const posthog = usePostHog();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);

    // Load saved countdown from localStorage
    const savedCountdown = localStorage.getItem(config.STORAGE_KEYS.FORGOT_PASSWORD_COUNTDOWN);
    const savedTimestamp = localStorage.getItem(config.STORAGE_KEYS.FORGOT_PASSWORD_TIMESTAMP);

    if (savedCountdown && savedTimestamp) {
      const now = Date.now();
      const elapsed = Math.floor((now - parseInt(savedTimestamp, 10)) / 1000);
      const remaining = Math.max(0, parseInt(savedCountdown, 10) - elapsed);

      if (remaining > 0) {
        setCountdown(remaining);
      } else {
        // Clear expired countdown
        localStorage.removeItem(config.STORAGE_KEYS.FORGOT_PASSWORD_COUNTDOWN);
        localStorage.removeItem(config.STORAGE_KEYS.FORGOT_PASSWORD_TIMESTAMP);
      }
    }
  }, []);

  // Countdown timer effect
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => {
        const newCountdown = countdown - 1;
        setCountdown(newCountdown);

        // Update localStorage with new countdown value
        if (newCountdown > 0) {
          localStorage.setItem(config.STORAGE_KEYS.FORGOT_PASSWORD_COUNTDOWN, newCountdown.toString());
        } else {
          // Clear localStorage when countdown reaches 0
          localStorage.removeItem(config.STORAGE_KEYS.FORGOT_PASSWORD_COUNTDOWN);
          localStorage.removeItem(config.STORAGE_KEYS.FORGOT_PASSWORD_TIMESTAMP);
        }
      }, 1000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [countdown]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleInputChange = (e) => {
    setEmail(e.target.value);
    // Clear error when user starts typing
    if (message) {
      setMessage('');
      setMessageType('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    capture(posthog, FORGOT_PASSWORD_PAGE_EVENT, { action: 'send_verification_code' });

    if (!email.trim()) {
      setMessageType('error');
      setMessage('Please enter your email address');
      return;
    }

    setIsLoading(true);
    setMessage('');
    setMessageType('');

    try {
      const response = await fetch(config.API_BASE_URL + config.API_ENDPOINTS.FORGOT_PASSWORD, {
        method: 'POST',
        headers: getApiHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ email: email.trim() }),
      });

      const data = await response.json();

      // Check for success response
      if (data.success) {
        setMessageType('success');
        setMessage('Verification code sent successfully! Please check your email inbox to proceed...');
        // Start 2-minute countdown
        setCountdown(120);
        // Save countdown to localStorage with timestamp
        localStorage.setItem(config.STORAGE_KEYS.FORGOT_PASSWORD_COUNTDOWN, '120');
        localStorage.setItem(config.STORAGE_KEYS.FORGOT_PASSWORD_TIMESTAMP, Date.now().toString());
      } else {
        // Handle error responses from API
        let errorMessage = data.message || 'Failed to send verification code. Please try again.';

        // Handle validation errors if they exist
        if (data.validation_errors && Array.isArray(data.validation_errors)) {
          errorMessage = data.validation_errors.join('\n');
        }

        setMessageType('error');
        setMessage(errorMessage);
      }
    } catch (err) {
      logger.error('Forgot password error:', err);
      setMessageType('error');
      setMessage('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    capture(posthog, FORGOT_PASSWORD_PAGE_EVENT, { action: 'back_to_login' });
    navigate('/login');
  };

  return (
    <main className={styles['forgot-container']} id="forgot-password-main" aria-label="Forgot password">
      <div className={styles['forgot-content']}>
        <div className={styles['logo-wrap']}>
          <BrandLogo alt="Richuel Logo" />
        </div>

        <div className={styles['forgot-form-container']}>
          <h1 className={styles['forgot-title']}>Forgot Password</h1>

          <div className={styles['forgot-instructions']}>
            <p>Enter your email address and we'll send you a verification code to reset your password.</p>
          </div>

          {message && (
            <div
              key={message}
              id="forgot-message"
              className={`${styles['error-message']} ${messageType === 'success' ? styles['success-message'] : ''}`}
              role="status"
              aria-live="polite"
            >
              {message.split('\n').map((msgLine, index) => (
                <div key={index}>{msgLine}</div>
              ))}
            </div>
          )}

          <form className={styles['forgot-form']} onSubmit={handleSubmit} aria-label="Request verification code">
            <div className={styles['form-group']}>
              <label htmlFor="forgot-email">Email</label>
              <input
                type="email"
                id="forgot-email"
                name="email"
                placeholder="Enter your email"
                value={email}
                onChange={handleInputChange}
                required
                disabled={isLoading}
                autoComplete="email"
                aria-describedby={message ? 'forgot-message' : undefined}
              />
            </div>

            <button
              type="submit"
              className={styles['submit-button']}
              disabled={isLoading || countdown > 0}
              aria-busy={isLoading}
              aria-label={isLoading ? 'Sending verification code' : countdown > 0 ? `Send verification code (available in ${formatTime(countdown)})` : 'Send verification code'}
            >
              {isLoading ? 'Sending...' : countdown > 0 ? `Send Verification Code (${formatTime(countdown)})` : 'Send Verification Code'}
            </button>
          </form>

          <div className={styles['back-to-login']}>
            <button
              type="button"
              onClick={handleBackToLogin}
              className={styles['back-button']}
              aria-label="Back to login page"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    </main>
  );
};

export default ForgotPassword; 